import React, { useState } from "react";
import { UserCheck, Sparkles, Send, RefreshCw, CheckCircle, AlertCircle, FileText, Award, HelpCircle, ArrowRight, Check, AlertTriangle } from "lucide-react";
import { AI_FEATURES_ENABLED } from "../../config/featureFlags";

interface InterviewCoachToolProps {
  apiFetch: (url: string, init?: RequestInit) => Promise<Response>;
  savedEmployers?: any[];
  userPreferences?: any;
  theme?: "light" | "dark";
  providerHealth?: { status: string; outageReason?: string | null };
  onProviderError?: (err: any) => void;
}

export default function InterviewCoachTool({
  apiFetch,
  savedEmployers = [],
  userPreferences,
  theme = "light",
  providerHealth,
  onProviderError
}: InterviewCoachToolProps) {
  const isDark = theme === "dark";
  const isCircuitOpen = providerHealth?.status === "outage";

  const [selectedEmployerId, setSelectedEmployerId] = useState<string>(
    savedEmployers[0]?.id || "custom"
  );

  const [customRoleTitle, setCustomRoleTitle] = useState("Front Desk / Customer Support");
  const [customEmployerName, setCustomEmployerName] = useState("Local Business");
  const [roleRequirements, setRoleRequirements] = useState("");
  const [userExperience, setUserExperience] = useState(
    userPreferences?.experience || "General job seeker with customer service and office skills"
  );

  const [inSession, setInSession] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [conversationHistory, setConversationHistory] = useState<
    { question: string; answer?: string; feedback?: any; context?: string }[]
  >([]);

  const [currentQuestion, setCurrentQuestion] = useState("");
  const [questionContext, setQuestionContext] = useState("");
  const [userAnswerInput, setUserAnswerInput] = useState("");
  const [latestFeedback, setLatestFeedback] = useState<any | null>(null);
  const [promptForRealExample, setPromptForRealExample] = useState<string | null>(null);

  const [summaryResult, setSummaryResult] = useState<any | null>(null);

  const getSelectedRolePayload = () => {
    if (selectedEmployerId !== "custom") {
      const found = savedEmployers.find((e) => String(e.id) === String(selectedEmployerId));
      if (found) {
        return {
          employerName: found.name,
          jobTitle: found.jobTitle || found.category || "Target Position",
          jobDescription: found.jobDescription || found.notes || "",
          requirements: roleRequirements || found.notes || ""
        };
      }
    }
    return {
      employerName: customEmployerName || "Target Employer",
      jobTitle: customRoleTitle || "Target Role",
      jobDescription: roleRequirements,
      requirements: roleRequirements
    };
  };

  const handleStartSession = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isLoading || isCircuitOpen) return;

    if (!AI_FEATURES_ENABLED) {
      setError("AI features are currently disabled.");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSummaryResult(null);
    setConversationHistory([]);

    const selectedRole = getSelectedRolePayload();

    try {
      const res = await apiFetch("/api/ai/interview-coach", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "start",
          selectedRole,
          userProfile: {
            experience: userExperience,
            skills: userPreferences?.skills || []
          },
          conversationHistory: []
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        if (data.providerError || res.status === 503 || res.status === 502) {
          if (onProviderError) onProviderError(data.providerError || data);
        }
        throw new Error(data.error || "AI tools are temporarily unavailable.\n\nYour request was not used, and no AI usage was consumed.\nPlease return later.");
      }

      setCurrentQuestion(data.data?.nextQuestion || "Tell me about your relevant experience for this role.");
      setQuestionContext(data.data?.questionContext || "");
      setPromptForRealExample(null);
      setLatestFeedback(null);
      setInSession(true);
    } catch (err: any) {
      console.error("Interview Coach error:", err);
      setError(err.message || "AI tools are temporarily unavailable.\n\nYour request was not used, and no AI usage was consumed.\nPlease return later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitAnswer = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!userAnswerInput.trim() || isLoading || isCircuitOpen) return;

    setIsLoading(true);
    setError(null);

    const updatedHistory = [
      ...conversationHistory,
      {
        question: currentQuestion,
        answer: userAnswerInput,
        context: questionContext
      }
    ];

    const selectedRole = getSelectedRolePayload();

    try {
      const res = await apiFetch("/api/ai/interview-coach", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "answer",
          selectedRole,
          userProfile: {
            experience: userExperience,
            skills: userPreferences?.skills || []
          },
          conversationHistory: updatedHistory,
          currentAnswer: userAnswerInput
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        if (data.providerError || res.status === 503 || res.status === 502) {
          if (onProviderError) onProviderError(data.providerError || data);
        }
        throw new Error(data.error || "AI tools are temporarily unavailable.\n\nYour request was not used, and no AI usage was consumed.\nPlease return later.");
      }

      setLatestFeedback(data.data?.feedback || null);
      setPromptForRealExample(data.data?.promptForRealExample || null);

      if (data.data?.nextQuestion) {
        setCurrentQuestion(data.data.nextQuestion);
        setQuestionContext(data.data.questionContext || "");
      }

      setConversationHistory(updatedHistory);
      setUserAnswerInput("");
    } catch (err: any) {
      console.error("Interview Coach Answer error:", err);
      setError(err.message || "AI tools are temporarily unavailable.\n\nYour request was not used, and no AI usage was consumed.\nPlease return later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinishAndGetSummary = async () => {
    if (isLoading || isCircuitOpen) return;
    setIsLoading(true);
    setError(null);

    const selectedRole = getSelectedRolePayload();

    try {
      const res = await apiFetch("/api/ai/interview-coach", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "summary",
          selectedRole,
          userProfile: {
            experience: userExperience,
            skills: userPreferences?.skills || []
          },
          conversationHistory
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        if (data.providerError || res.status === 503 || res.status === 502) {
          if (onProviderError) onProviderError(data.providerError || data);
        }
        throw new Error(data.error || "AI tools are temporarily unavailable.\n\nYour request was not used, and no AI usage was consumed.\nPlease return later.");
      }

      setSummaryResult(data.data);
      setInSession(false);
    } catch (err: any) {
      console.error("Interview Coach Summary error:", err);
      setError(err.message || "AI tools are temporarily unavailable.\n\nYour request was not used, and no AI usage was consumed.\nPlease return later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 text-left">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 md:p-6 shadow-xs">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl">
            <UserCheck className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">AI Interview Coach</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Adaptive 1-on-1 practice interview based on real employer requirements and your actual experience.
            </p>
          </div>
        </div>

        {!inSession && !summaryResult && (
          <form onSubmit={handleStartSession} className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Target Employer</label>
                <select
                  disabled={isLoading || isCircuitOpen}
                  value={selectedEmployerId}
                  onChange={(e) => setSelectedEmployerId(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {savedEmployers.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name} — {emp.category || "Saved Business"}
                    </option>
                  ))}
                  <option value="custom">+ Custom Target Position</option>
                </select>
              </div>

              {selectedEmployerId === "custom" && (
                <>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Employer Name</label>
                    <input
                      type="text"
                      disabled={isLoading || isCircuitOpen}
                      value={customEmployerName}
                      onChange={(e) => setCustomEmployerName(e.target.value)}
                      placeholder={isCircuitOpen ? "AI tools are temporarily down..." : "e.g. City Dental Group"}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Role Title</label>
                    <input
                      type="text"
                      disabled={isLoading || isCircuitOpen}
                      value={customRoleTitle}
                      onChange={(e) => setCustomRoleTitle(e.target.value)}
                      placeholder={isCircuitOpen ? "AI tools are temporarily down..." : "e.g. Office Administrator"}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                </>
              )}

              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Your Real Experience Summary</label>
                <textarea
                  rows={2}
                  disabled={isLoading || isCircuitOpen}
                  value={userExperience}
                  onChange={(e) => setUserExperience(e.target.value)}
                  placeholder={isCircuitOpen ? "AI tools are temporarily down..." : "e.g. 2 years front-desk administrative work, appointment scheduling, phone support"}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || isCircuitOpen}
              className="w-full sm:w-auto px-6 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-400 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-extrabold text-xs rounded-xl transition shadow-xs flex items-center justify-center gap-2 cursor-pointer"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Preparing Custom Questions...</span>
                </>
              ) : isCircuitOpen ? (
                <>
                  <AlertTriangle className="w-4 h-4 text-amber-300" />
                  <span>AI Service Temporarily Unavailable</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 text-amber-300" />
                  <span>Start Practice Interview Session</span>
                </>
              )}
            </button>
          </form>
        )}
      </div>

      {error && (
        <div className="p-4 bg-amber-50 dark:bg-amber-950/50 border border-amber-300 dark:border-amber-800 rounded-2xl flex items-start gap-3 text-xs text-amber-950 dark:text-amber-100 shadow-xs">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="space-y-1 min-w-0 flex-1">
            <p className="font-bold text-amber-900 dark:text-amber-200">AI Service Notice</p>
            <p className="leading-relaxed whitespace-pre-line">{error}</p>
          </div>
        </div>
      )}

      {/* Active Session Display */}
      {inSession && (
        <div className="space-y-6">
          {/* Latest Feedback Box */}
          {latestFeedback && (
            <div className="bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white rounded-2xl p-5 border border-slate-200 dark:border-slate-800 space-y-3">
              <span className="text-[10px] font-black uppercase text-indigo-700 dark:text-indigo-400 tracking-wider block">
                AI Feedback on Previous Answer
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-slate-500 dark:text-slate-400 block text-[11px]">Clarity & Relevance:</span>
                  <span className="font-bold text-slate-900 dark:text-white">{latestFeedback.clarity || "Clear"}</span>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400 block text-[11px]">Real Example Used:</span>
                  <span className={`font-bold ${latestFeedback.usedRealExample ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>
                    {latestFeedback.usedRealExample ? "Yes — Concrete example provided" : "General answer — Example recommended"}
                  </span>
                </div>
              </div>
              {latestFeedback.detailsToImprove && (
                <p className="text-xs text-slate-700 dark:text-slate-300 pt-2 border-t border-slate-200 dark:border-slate-800">
                  <strong className="text-amber-300">How to strengthen:</strong> {latestFeedback.detailsToImprove}
                </p>
              )}
            </div>
          )}

          {/* Missing Real Situation Prompt */}
          {promptForRealExample && (
            <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-2xl flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900 dark:text-amber-200">
                <span className="font-extrabold block mb-1">Add a Real Example:</span>
                {promptForRealExample}
              </div>
            </div>
          )}

          {/* Current Question Display */}
          <div className="bg-white dark:bg-slate-900 border-2 border-indigo-500/30 rounded-2xl p-6 shadow-md space-y-4">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-1 bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 text-[10px] font-black uppercase tracking-wider rounded-md">
                Question {conversationHistory.length + 1}
              </span>
              <span className="text-xs text-slate-500">{questionContext}</span>
            </div>

            <h3 className="text-base font-black text-slate-900 dark:text-white leading-snug">
              {currentQuestion}
            </h3>

            <form onSubmit={handleSubmitAnswer} className="space-y-3">
              <textarea
                rows={4}
                disabled={isLoading || isCircuitOpen}
                value={userAnswerInput}
                onChange={(e) => setUserAnswerInput(e.target.value)}
                placeholder={isCircuitOpen ? "AI tools are temporarily down..." : "Type your answer here using your real experience..."}
                className="w-full p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 leading-relaxed disabled:opacity-60 disabled:cursor-not-allowed"
              />

              <div className="flex flex-wrap justify-between items-center gap-3">
                <button
                  type="submit"
                  disabled={isLoading || isCircuitOpen || !userAnswerInput.trim()}
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white font-extrabold text-xs rounded-xl shadow-xs transition flex items-center gap-2 cursor-pointer"
                >
                  {isLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Evaluating Answer...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Submit Answer</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleFinishAndGetSummary}
                  disabled={isLoading || isCircuitOpen || conversationHistory.length === 0}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-400 dark:disabled:bg-slate-800 text-white font-bold text-xs rounded-xl transition flex items-center gap-1.5 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Award className="w-4 h-4 text-amber-400" />
                  <span>Finish & Get Prep Summary</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preparation Summary View */}
      {summaryResult && (
        <div className="space-y-6">
          <div className={`${
            isDark
              ? "bg-slate-900 border-indigo-800/50 text-white"
              : "bg-blue-50/80 border-blue-200 text-slate-900"
          } rounded-2xl p-6 shadow-sm border space-y-2`}>
            <span className={`${isDark ? "text-indigo-400" : "text-blue-700"} text-[10px] uppercase font-black tracking-wider block`}>
              Interview Practice Complete
            </span>
            <h3 className={`text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>AI Interview Preparation Summary</h3>
            <p className={`text-xs ${isDark ? "text-slate-300" : "text-slate-600"}`}>
              Review your key strengths, areas needing detail, questions to practice again, and your final interview checklist.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Strong Areas */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-3">
              <h4 className="text-xs font-black uppercase text-emerald-600 dark:text-emerald-400 tracking-wider flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Strong Answer Areas
              </h4>
              <ul className="space-y-1.5 text-xs text-slate-700 dark:text-slate-300 font-medium">
                {summaryResult.strongAnswerAreas?.map((s: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-emerald-500 font-bold">•</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Areas Needing Detail */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-3">
              <h4 className="text-xs font-black uppercase text-amber-600 dark:text-amber-400 tracking-wider flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> Areas Needing More Detail
              </h4>
              <ul className="space-y-1.5 text-xs text-slate-700 dark:text-slate-300 font-medium">
                {summaryResult.areasNeedingDetail?.map((a: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-amber-500 font-bold">•</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Real Examples & Questions to Ask Employer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-3">
              <h4 className="text-xs font-black uppercase text-indigo-600 dark:text-indigo-400 tracking-wider flex items-center gap-2">
                <FileText className="w-4 h-4" /> Real Examples You Should Prepare
              </h4>
              <ul className="space-y-1.5 text-xs text-slate-700 dark:text-slate-300 font-medium">
                {summaryResult.realExamplesToPrepare?.map((e: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-indigo-500 font-bold">•</span>
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-3">
              <h4 className="text-xs font-black uppercase text-violet-600 dark:text-violet-400 tracking-wider flex items-center gap-2">
                <HelpCircle className="w-4 h-4" /> Great Questions to Ask Employer
              </h4>
              <ul className="space-y-1.5 text-xs text-slate-700 dark:text-slate-300 font-medium">
                {summaryResult.questionsToAskEmployer?.map((q: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-violet-500 font-bold">•</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Final Checklist */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-3">
            <h4 className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" /> Final Interview Checklist
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {summaryResult.finalChecklist?.map((item: string, i: number) => (
                <div key={i} className="p-2.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-100 dark:border-slate-800 text-xs text-slate-800 dark:text-slate-200 flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
